"use client";

// The company page — blog · inside innernet (the dispatch archive), on a lavender ground
// with the organization's red netti living on the right. The navigator grammar
// all the way down: section ticker, dek, stat line, view chips (posts ·
// timeline), frosted post cards that swipe on mobile.

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import OrganismBackdrop from "../OrganismBackdrop";
import SiteMenu, { MENU_ORG, MENU_ORG_SCALE } from "../SiteMenu";
import CompanySearch from "./CompanySearch";
import CompanyTicker from "./CompanyTicker";
import { Byline } from "./MediaThumb";
import PostFace from "./PostFace";
import AboutBody from "./AboutBody";
import TeamBody from "./TeamBody";
import { CO_DEKS, CO_TAB_KINDS, companyTabs, type CoTab } from "./companyTheme";
import { ABOUT_THEME, CO_THEMES, TEAM_THEME, type CoRoom, type CoTheme } from "../../lib/company/theme";
import { useCoTheme } from "./useCoTheme";
import { useNaturalHeight } from "../../lib/os/useNaturalHeight";
import Stage from "../stage/Stage";
import { useStageReadRamp } from "../stage/useStage";
import "../stage/surfaces/company.css";
import { relTime } from "../../lib/os/tree";
import type { MenuSpace } from "../../lib/nav/siteNav";
import type { CompanyPost } from "../../lib/company/types";
import type { AuthorMap } from "../../lib/company/authors";
import type { CompanyViewer } from "../../lib/company/data";
import { readerOrg, INDEX_ORG_WIDE, FULL_BLOOM_AGE } from "../../lib/os/readerOrganism";

/** what the rail can stand on: a stream, or one of the two night rooms read in place */
type RailTab = CoTab | "about" | "team";

/** narrow-viewport flag for the menu-open organism scale (the dashboard's split). */
export function useIsNarrow(bp = 720): boolean {
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${bp}px)`);
    const on = () => setNarrow(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [bp]);
  return narrow;
}

export default function CompanyClient({
  posts,
  viewer,
  authors,
  initialTab,
}: {
  posts: CompanyPost[];
  viewer: CompanyViewer | null;
  /** author name → display name + avatar, resolved server-side */
  authors: AuthorMap;
  initialTab?: string;
}) {
  const founder = !!viewer?.founder;
  const router = useRouter();
  // compare is a section of the company too — a page of its own, so selecting it
  // in the ticker LEAVES rather than filters (the router, below). about and team
  // are NOT pages of their own any more: they are read here, under this rail.
  const tabs = [...companyTabs(founder), { key: "compare", label: "compare" }, { key: "about", label: "about" }, { key: "team", label: "team" }];
  // land on the requested tab; otherwise the BLOG, which is the company's front
  // door. It falls through to the first stream that actually has posts only when
  // the blog itself is empty, so a visitor never meets a blank section.
  const kindsFor = (key: string): string[] => CO_TAB_KINDS[key as CoTab] ?? ["blog"];
  const hasPosts = (key: string) => posts.some((p) => kindsFor(key).includes(p.kind));
  const preferred = hasPosts("blog")
    ? "blog"
    : tabs.find((t) => t.key !== "compare" && t.key !== "about" && hasPosts(t.key))?.key;
  const [tab, setTab] = useState<RailTab>(
    tabs.some((t) => t.key === initialTab) ? (initialTab as RailTab) : ((preferred as RailTab) ?? "blog"),
  );
  const [view, setView] = useState<"posts" | "timeline">("posts");
  // a category lens over the current tab — the views grow from the data itself
  // (subcategory, else category), so a new category simply appears as posts land.
  const [cat, setCat] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const isNarrow = useIsNarrow();
  // on a phone the creature goes to the bottom centre and back UNDER the veil —
  // there is no field beside the words to stand in (lib/os/readerOrganism).
  const org = readerOrg(isNarrow, INDEX_ORG_WIDE);
  // THE ROOM FOLLOWS THE STREAM. Switching the ticker from blog to updates now
  // changes the ground and the creature with it, so the two sections are
  // distinguishable from across the room instead of being one lavender page
  // with different words on it. The organism eases between them on its own.
  //
  // ABOUT AND TEAM ARE THE NIGHT ROOMS, and they are read IN THE RAIL. They
  // used to be pages of their own: selecting the word in the ticker was a full
  // reload onto a different-looking page whose title stood where the rail had
  // been, so the company's own navigator vanished the moment you asked about
  // the company. Now the rail stays, the room goes dark around it (`.co-night`
  // restates the ink ladder for the dark ground), and the head collapses on
  // scroll exactly as it does over the letters.
  const NIGHT: Partial<Record<string, CoTheme>> = { about: ABOUT_THEME, team: TEAM_THEME };
  // THE URL IS THE TRUTH (2026-09-20). A tab switch writes the address with
  // history.replaceState — the router's own state never moves — so a later
  // press of a LINK to an address we are "already on" (the crumb's `company`,
  // the ≡ menu's `company` row, the browser's back button) changed the address
  // and nothing else: the page kept showing the tab you were on. Reading the
  // address back means every one of those doors works, from anywhere.
  const pathname = usePathname();
  const search = useSearchParams();
  useEffect(() => {
    const fromPath = pathname === "/company/about" ? "about" : pathname === "/company/team" ? "team" : null;
    const wanted = (fromPath ?? search.get("tab") ?? "blog") as RailTab;
    if (!tabs.some((t) => t.key === wanted)) return;
    setTab((cur) => {
      if (cur === wanted) return cur;
      setCat(null);
      return wanted;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, search]);

  const night = NIGHT[tab];
  const theme = night ?? CO_THEMES[(tab as CoRoom)] ?? CO_THEMES.blog;
  // a stream lists posts; a night room carries its own body
  const isStream = !night;
  useCoTheme(theme.ground);
  // how the creature stands in each room — the night rooms open it far wider,
  // because there netti IS the room (the numbers the standalone pages wore).
  const orgLook =
    tab === "about"
      ? { size: "min(200vmin, 2100px)", opacity: 0.8, fade: 0.9 }
      : tab === "team"
        ? { size: "min(170vmin, 1800px)", opacity: 0.74, fade: 0.9 }
        : { size: "min(150vmin, 1520px)", opacity: 0.97, fade: 0.88 };

  useEffect(() => {
    const c = new URLSearchParams(window.location.search).get("cat");
    if (c) setCat(c);
  }, []);

  // the page ground — the organism engine reads the body color every frame, so
  // painting it there keeps the bloom's composite seamless.
  // law_read_ramp — as you scroll the list to read, the .co-read-scrim settles
  // the ground (opacity IS --art-ramp) and clears again at the top. law_full_bloom (2026-09-26): netti here is always fully grown and sharp. The ramp only settles the GROUND behind it and the head's band; it never frosts, ages or rests the creature.
  // (on the stage: it hears the stage's scroller — components/stage/useStage)
  useStageReadRamp(220, "art");

  // law_stage — the head is the stage's navigator: the stage folds it (with the
  // scroll-up reveal); its folding half publishes its natural height.
  const foldRef = useRef<HTMLDivElement>(null);
  useNaturalHeight(foldRef, "--lock-h", tab);
  // the direction the content slides in from — the side the chosen tab sits on
  // relative to the current one in the ticker (wrapping reads the short way).
  const [navDir, setNavDir] = useState(1);

  const syncUrl = (key: string, c: string | null) => {
    // the night rooms keep their own addresses (they have cards, metadata and
    // a place in the sitemap of their own); the streams are ?tab= on the index.
    if (key === "about" || key === "team") {
      write(`/company/${key}`);
      return;
    }
    const params = new URLSearchParams();
    if (key !== "blog") params.set("tab", key);
    if (c) params.set("cat", c);
    const qs = params.toString();
    write(qs ? `/company?${qs}` : "/company");
  };
  // a stream is a PLACE (law_every_place_an_address): a new tab is a new entry,
  // so Back walks the streams; a category inside one only renames the entry.
  const write = (url: string) => {
    const here = window.location.pathname + window.location.search;
    if (url === here) return;
    const sameStream = url.split("?")[0] === window.location.pathname && new URLSearchParams(url.split("?")[1] ?? "").get("tab") === new URLSearchParams(window.location.search).get("tab");
    if (sameStream) window.history.replaceState(null, "", url);
    else window.history.pushState(null, "", url);
  };
  useEffect(() => {
    const onPop = () => {
      const seg = window.location.pathname.split("/")[2];
      const q = new URLSearchParams(window.location.search);
      const key = seg === "about" || seg === "team" ? seg : q.get("tab") ?? "blog";
      if (!tabs.some((t) => t.key === key)) return;
      setTab((cur) => {
        const from = tabs.findIndex((t) => t.key === cur);
        const to = tabs.findIndex((t) => t.key === key);
        const delta = to - from;
        setNavDir(Math.abs(delta) === tabs.length - 1 ? (delta > 0 ? -1 : 1) : Math.sign(delta) || 1);
        return key as RailTab;
      });
      setCat(q.get("cat"));
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const selectTab = (key: string) => {
    // compare is a PAGE, not a stream — selecting it leaves, on the client
    // router (a full reload here was one of the "long delays": the whole
    // document, the veil and the creature again, for a sibling section).
    if (key === "compare") { router.push("/company/compare"); return; }
    const from = tabs.findIndex((t) => t.key === tab);
    const to = tabs.findIndex((t) => t.key === key);
    const delta = to - from;
    setNavDir(Math.abs(delta) === tabs.length - 1 ? (delta > 0 ? -1 : 1) : Math.sign(delta) || 1);
    setTab(key as RailTab);
    setCat(null); // a tab switch clears the category lens
    syncUrl(key, null);
  };
  const selectCat = (c: string | null) => {
    setCat(c);
    syncUrl(tab, c);
  };

  // the updates tab is the versioned changelog — only real releases (a `version`),
  // never a stray dispatch that predates the kind='inside' move.
  const tabList = isStream
    ? posts.filter((p) => kindsFor(tab).includes(p.kind) && (tab !== "updates" || !!p.version))
    : [];
  // the category a post files under, for the lens: subcategory first, else category.
  const catOf = (p: CompanyPost) => (p.subcategory ?? p.category ?? "").toLowerCase();
  const list = cat ? tabList.filter((p) => catOf(p) === cat) : tabList;
  const latest = list.find((p) => p.published_at)?.published_at ?? null;

  // the "company" space — this page's own sections. Selecting one switches the
  // tab in place (the menu closes itself).
  const currentSpace: MenuSpace = {
    id: "company",
    label: "company",
    items: tabs.map((t) => ({ key: t.key, label: t.label, here: t.key === tab, onSelect: () => selectTab(t.key) })),
  };

  return (
    // --bg carries the page's real ground so the menu veil + every frosted
    // surface wear the lavender, not the default cream.
    <main className={`co-page${night ? " co-night" : ""}`} style={{ ["--bg" as string]: theme.ground } as React.CSSProperties}>
      {/* the room's netti — at rest it lives on the right; while the menu is open it
          takes centre stage with the EXACT dashboard choreography (MENU_ORG). */}
      <OrganismBackdrop
        fixed
        anchor={menuOpen ? MENU_ORG.anchor : org.anchor}
        offset={menuOpen ? MENU_ORG.offset : org.offset}
        glide={menuOpen ? MENU_ORG.glide : undefined}
        glideAxis={menuOpen ? MENU_ORG.glideAxis : undefined}
        size={orgLook.size}
        blur={0}
        opacity={menuOpen ? MENU_ORG.opacity : orgLook.opacity}
        fade={orgLook.fade}
        scale={menuOpen ? (isNarrow ? MENU_ORG_SCALE.narrow : MENU_ORG_SCALE.wide) : 1}
        // above the veil, like the dashboard — the scrim frosts the ground, not
        // the creature (z1 veil · z2 netti · z3 the words).
        zIndex={menuOpen ? MENU_ORG.zIndex : org.zIndex}
        innerTransMs={menuOpen ? MENU_ORG.innerTransMs : undefined}
        innerEase={menuOpen ? MENU_ORG.innerEase : undefined}
        color={theme.color}
        age={FULL_BLOOM_AGE}
        spin
        publishHue
        live={false}
      />

      {/* the reading ground — settles behind the words; netti stays above it, whole */}
      <div className="co-read-scrim" aria-hidden />

      <SiteMenu
        trail={night ? [{ label: "company", href: "/company" }, { label: tab }] : [{ label: "company" }]}
        founder={founder}
        signedIn={!!viewer}
        current={currentSpace}
        hereKey="company"
        search={<CompanySearch />}
        onOpenChange={setMenuOpen}
      />

      {/* law_stage — the head is the stage's navigator, over the top of the
          content's own scroll area; the stage folds it (the stream ticker and the
          view chips stay, the dek and the stat line fold away), reveals it on a
          scroll up, and hands each stream over in the direction of the ticker */}
      <Stage
        frame="none"
        autoFold
        top="clamp(4.6rem, 11vh, 6.4rem)"
        pin="clamp(4.2rem, 8.6vh, 4.75rem)"
        gap="clamp(1.6rem, 4vh, 2.6rem)"
        headClassName="os-lock--detached"
        sceneKey={tab}
        dir={navDir >= 0 ? 1 : -1}
        swipe
        label={tab}
        rootProps={{ className: "stage--company" }}
        head={
          <div className="co-shell stage-company__head">
            <header className="co-head co-head--menu">
              <CompanyTicker tabs={tabs} active={tab} onSelect={selectTab} />
              <div ref={foldRef} className="os-lock__fold co-head__fold">
                <p className={`co-dek${night ? " ab-dek" : ""}`}>{CO_DEKS[tab]}</p>
                {isStream && (
                  <div className="co-meta">
                    <span>{list.length} {list.length === 1 ? "post" : "posts"}</span>
                    {latest && (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "0.32rem" }}>
                        <span className="ink-dot" aria-hidden />
                        <b>{relTime(latest)}</b>
                      </span>
                    )}
                  </div>
                )}
              </div>
              {tabList.length > 0 && (
                <div className="co-chips">
                  {(["posts", "timeline"] as const).map((v) => (
                    <button key={v} type="button" className="chip" data-on={view === v ? "true" : undefined} onClick={() => setView(v)}>
                      {v}
                    </button>
                  ))}
                  {viewer?.editor && (
                    <Link className="chip chip-plus" href="/company/compose">+ compose</Link>
                  )}
                </div>
              )}
              {/* No category lens. The streams above (inside innernet · updates ·
                  blog) are the only division the reader needs; a second row of
                  chips underneath them split the same small archive twice and read
                  as filter machinery rather than a place to read. A post still
                  carries its category — on its byline, and in the crumb when you
                  open it — it just isn't a control here. */}
            </header>
          </div>
        }
      >
        <div className="co-shell stage-company__body">

            {tab === "about" ? (
              <AboutBody signedIn={!!viewer} />
            ) : tab === "team" ? (
              <TeamBody signedIn={!!viewer} />
            ) : (
            <div className="co-body">
              {list.length === 0 ? (
                <p className="co-empty">
                  {tab === "inside"
                    ? "a look inside how innernet is built — the people, the laws, the taste. soon."
                    : tab === "reports"
                      ? "no readings yet. the first one is drafted on the 8th of the month, the day innernet launched."
                      : "nothing here yet."}
                </p>
              ) : view === "posts" ? (
                <div className="co-grid">
                  {list.map((p) => (
                    <Link key={p.id} className="co-card" href={`/company/${p.slug}`}>
                      {/* a post with no media wears its own auto-designed thumbnail —
                          netti on a matching sweep, seeded from the slug */}
                      <span className="co-card__media" data-empty={!p.media[0] || undefined}>
                        <PostFace post={p} author={authors[(p.author ?? "").toLowerCase()] ?? null} />
                      </span>
                      <span className="co-card__t">{p.title}</span>
                      {p.dek && <span className="co-card__d">{p.dek}</span>}
                      <Byline author={p.author} category={p.category} when={relTime(p.published_at ?? p.created_at)} draft={p.status === "draft"} />
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="co-rows">
                  {list.map((p, i) => (
                    <Link key={p.id} className="co-row" href={`/company/${p.slug}`}>
                      <span className="co-row__thumb" data-empty={!p.media[0] || undefined}>
                        <PostFace post={p} author={authors[(p.author ?? "").toLowerCase()] ?? null} className="dthumb--mini" />
                      </span>
                      <span style={{ minWidth: 0 }}>
                        <span className="co-row__t">{p.title}</span>
                        {p.dek && <span className="co-row__d">{p.dek}</span>}
                        <Byline author={p.author} category={p.category} when={relTime(p.published_at ?? p.created_at)} draft={p.status === "draft"} />
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
            )}
        </div>
      </Stage>
    </main>
  );
}
